import { Component, Input, OnInit, ViewChild, signal } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { onSnapshot, collection, query, orderBy, deleteDoc, doc, addDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { ucFirst } from 'src/app/globals';
import { GenericWarningDialogData, PhoneNumber, RidePreparationLog } from 'src/app/interfaces';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-phone-numbers',
  templateUrl: './phone-numbers.component.html',
  styleUrl: './phone-numbers.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    MatProgressSpinner,
  ],
})
export class PhoneNumbersComponent implements OnInit {
  @Input() parentData: { processing: boolean };
  @ViewChild('table', { static: false }) table: MatTable<any>;

  phoneNumbers = signal<PhoneNumber[]>([]);
  filteredPhoneNumbers = signal<PhoneNumber[]>([]);
  displayedColumns = ['name', 'order', 'phone', 'button'];
  totalPhoneNumbers = signal<number>(0);
  orderNumbersArray = signal<number[]>([]);
  searchCtrl = new UntypedFormControl();
  organisationId = dataStore.selectedOrganisation.id;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/numbers`), orderBy('order')),
      (snapshot) => {
        const phoneNumbers: PhoneNumber[] = [];
        const orderNumbers: number[] = [];
        this.totalPhoneNumbers.set(snapshot.size);
        snapshot.forEach((doc) => {
          const data = doc.data() as PhoneNumber;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          phoneNumbers.push({ id, ...data });
          if (data.order !== null && data.order !== undefined) {
            orderNumbers.push(Number(data.order));
          }
        });
        this.phoneNumbers.set(phoneNumbers);
        this.orderNumbersArray.set(orderNumbers);
        this.filteredPhoneNumbers.set(this.filterPhoneNumbers(this.searchCtrl.value, phoneNumbers));
        if (this.table) {
          this.table.renderRows();
        }
      },
    );
    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredPhoneNumbers.set(this.filterPhoneNumbers(value, this.phoneNumbers()));
      if (this.table) {
        this.table.renderRows();
      }
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterPhoneNumbers(value: string, array: PhoneNumber[]): PhoneNumber[] {
    if (!value || value === '') {
      return array;
    }
    const filterValue = value.toLowerCase();
    return array.filter((number) => number.name.toLowerCase().includes(filterValue));
  }

  editItem(phoneNumber?: PhoneNumber) {
    const fields: Field[] = [
      {
        key: 'name',
        type: 'string',
        label: 'Naam',
        required: true,
      },
      {
        key: 'phone',
        type: 'phone',
        label: 'Telefoonnummer',
        required: true,
      },
      {
        key: 'order',
        type: 'order',
        label: 'Volgordenummer',
        required: true,
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '400px',
      data: {
        item: phoneNumber,
        itemLabel: 'telefoonnummer',
        fields: fields,
        optionalData: { orderNumbersArray: this.orderNumbersArray() },
      },
    });
  }

  deleteItem(phoneNumber?: PhoneNumber) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deletePhoneNumber',
        optionalData: {
          phoneNumber: phoneNumber.phone,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await deleteDoc(doc(db, `organisations/${this.organisationId}/numbers/${phoneNumber.id}`));
        await this.logDelete(phoneNumber);
        this.snackBar.open('Het telefoonnummer is verwijderd.', 'X', {
          duration: 5000,
        });
      }
    });
  }

  getExportData() {
    const phoneNumbers = this.phoneNumbers().map((phoneNumber) => phoneNumber as PhoneNumber);
    const columns = [
      { name: 'Naam', itemProperty: 'name' },
      { name: 'Telefoonnummer', itemProperty: 'phone' },
      { name: 'Volgordenummer', itemProperty: 'order' },
    ];
    return { items: phoneNumbers, columns: columns, options: { sortBy: 'Naam' } };
  }

  getImportConfig() {
    const columnSettings = [
      { columnName: 'naam', itemProperty: 'name', required: true },
      { columnName: 'telefoonnummer', itemProperty: 'phone', required: true },
      {
        columnName: 'volgordenummer',
        itemProperty: 'order',
        type: 'number',
        required: true,
        min: 0,
      },
    ];
    const existingData = this.phoneNumbers().map((phoneNumber) => phoneNumber as PhoneNumber);

    return {
      tab: 'phoneNumbers',
      uniqueKey: 'name',
      organisationId: this.organisationId,
      columnSettings: columnSettings,
      existingData: existingData,
    };
  }

  async logDelete(item: PhoneNumber) {
    if (!item) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: LogType.RidePreparationPhoneNumber,
      date: new Date(),
      action: 'delete',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: 'telefoonnummer',
      itemId: item.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      before: { ...item },
      after: null,
    };
    delete logObj.before.id;

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }
}
